import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { FieldShell } from "./FieldShell";
import { FOCUS_RING, fieldIds, resolveFieldState } from "./fieldStyles";

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  error?: string | null;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  size?: "default" | "sm";
  containerClassName?: string;
  children?: never;
}

const STATE_CLASSES = {
  default: "border-gray-300 bg-white",
  error: "border-danger bg-danger-bg",
  readonly: "border-gray-200 bg-gray-50",
  overridden: "border-brand-green bg-brand-green-05",
  synthetic: "border-gray-200 bg-gray-50",
} as const;

/** Headless UI Listbox styled like the other fields. Never a native select. */
export function SelectInput({ id, label, value, onChange, options, error, hint, required, placeholder = "Select...", disabled, size = "default", containerClassName }: SelectInputProps) {
  const ids = fieldIds(id, error, hint);
  const state = resolveFieldState({ error, readOnly: disabled });
  const selected = options.find((option) => option.value === value);
  const isPlaceholder = !selected || selected.value === "";
  const sizing = size === "sm" ? "pl-3 pr-8 py-1.5 text-sm" : "pl-3 pr-8 py-2.5 text-sm";

  return (
    <FieldShell id={id} label={label} required={required} hint={hint} hintId={ids.hintId} error={error} errorId={ids.errorId} containerClassName={containerClassName}>
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        <div className="relative">
          <ListboxButton
            id={id}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={ids.describedBy}
            className={`w-full rounded-lg border ${sizing} text-left cursor-pointer disabled:cursor-default ${FOCUS_RING} ${STATE_CLASSES[state]} ${isPlaceholder ? "text-gray-400" : "text-gray-900"}`}
          >
            <span className="block truncate">{isPlaceholder ? placeholder : selected?.label}</span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronDown size={16} className="text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>
          <ListboxOptions
            anchor="bottom start"
            modal={false}
            className="z-50 max-h-60 w-[var(--button-width)] overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none [--anchor-gap:4px]"
          >
            {options.map((option) => (
              <ListboxOption key={option.value} value={option.value} className="group relative cursor-pointer select-none py-2.5 pl-10 pr-4 text-gray-900 data-[focus]:bg-brand-green-10 data-[focus]:text-brand-green">
                {({ selected: isSelected }) => (
                  <>
                    {isSelected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-green">
                        <Check size={16} aria-hidden="true" />
                      </span>
                    )}
                    <span className={`block truncate ${isSelected ? "font-semibold" : "font-normal"}`}>{option.value === "" ? placeholder : option.label}</span>
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </FieldShell>
  );
}
